import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { ErrorText, Input, Modal } from '@/shared/ui';

import { analyticsModelMessages } from '../../config/i18n';

import styles from './AnalyticsModelSettingsPopup.module.scss';

export type AnalyticsModelSettingsDraft = {
  code: string;
};

export type AnalyticsModelSettingsErrors = {
  code?: string;
  global?: string;
};

type AnalyticsModelSettingsPopupProps = {
  code: string;
  isOpen: boolean;
  errors?: AnalyticsModelSettingsErrors;
  onClose: () => void;
  onSave: (settings: AnalyticsModelSettingsDraft) => void;
};

export const AnalyticsModelSettingsPopup: FC<
  AnalyticsModelSettingsPopupProps
> = (props) => {
  const { code, isOpen, errors, onClose, onSave } = props;

  const { formatMessage } = useIntl();

  const [localCode, setLocalCode] = useState(code);
  const [fieldErrors, setFieldErrors] = useState<{
    code?: string;
  }>({});
  const [globalError, setGlobalError] = useState<string | undefined>();

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setLocalCode(code);
    setFieldErrors({
      code: errors?.code,
    });
    setGlobalError(errors?.global);
  }, [code, errors, isOpen]);

  const handleCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    setLocalCode(nextValue);
    setFieldErrors((prev) => ({
      ...prev,
      code: nextValue.trim() ? undefined : prev.code,
    }));
    setGlobalError(undefined);
  };

  const handleSave = () => {
    const nextErrors: { code?: string } = {};

    if (!localCode.trim()) {
      nextErrors.code = formatMessage(analyticsModelMessages.codePlaceholder);
    }

    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }

    onSave({
      code: localCode.trim(),
    });
  };

  const isSaveDisabled = !localCode.trim();

  return (
    <Modal
      data-component="analytics-model-settings-popup"
      isOpen={isOpen}
      title={formatMessage(analyticsModelMessages.advanced)}
      okButtonText={formatMessage(analyticsModelMessages.save)}
      cancelButtonText={formatMessage(analyticsModelMessages.cancel)}
      onOk={handleSave}
      onCancel={onClose}
      okButtonProps={{ disabled: isSaveDisabled }}
    >
      <div className={styles.container}>
        <ErrorText className={styles.globalError} error={globalError} />

        <div className={styles.field}>
          <Input
            name="analyticsModelCode"
            label={formatMessage(analyticsModelMessages.code)}
            value={localCode}
            onChange={handleCodeChange}
            required
            error={fieldErrors.code}
            placeholder={formatMessage(analyticsModelMessages.codePlaceholder)}
          />
        </div>
      </div>
    </Modal>
  );
};
